import {DialogResult} from "@/lib/dialog-orchestrator/types";

/**
 * `close_type` for `popup_closed` (AC-711, spec §9.1).
 *
 * The orchestrator reports *why* a dialog closed (`success` + `reason`), which
 * separates `success` and `system` cleanly. What it cannot see is *how* the user
 * dismissed it — Radix reports the X button and an outside click through the very
 * same `onOpenChange(false)` callback.
 *
 * Rather than thread a close reason through all 18 dialogs, this module watches
 * the last user interaction before the close:
 *
 *   pointerdown inside the dialog content → `x_button`
 *   pointerdown outside it                → `outside_click`
 *   Escape                                → `outside_click`
 *
 * It is a HEURISTIC, and deliberately so: it infers intent instead of observing
 * it, and can be wrong (e.g. a programmatic close that happens to land shortly
 * after an unrelated click). `close_type` is a funnel-diagnostic property, not a
 * load-bearing one — a small error rate is an acceptable price for zero edits to
 * the dialog components. The staleness window below bounds how wrong it can get.
 */

export type CloseType = "x_button" | "outside_click" | "system" | "success";

/**
 * How long a recorded interaction can be used to explain a close. Beyond this,
 * the close was almost certainly not caused by that click, and we report
 * `system` rather than guess.
 */
const INTENT_TTL_MS = 1_000;

/** Radix/vaul content roots. A pointerdown inside one of these is "in the dialog". */
const DIALOG_CONTENT_SELECTOR = '[role="dialog"], [role="alertdialog"]';

interface CloseIntent {
    type: "x_button" | "outside_click";
    at: number;
}

let lastIntent: CloseIntent | null = null;

const onPointerDown = (event: PointerEvent): void => {
    const target = event.target;
    const inDialog =
        target instanceof Element && target.closest(DIALOG_CONTENT_SELECTOR) !== null;

    lastIntent = {type: inDialog ? "x_button" : "outside_click", at: Date.now()};
};

const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape") return;
    // Escape and an overlay click are the same user intent — "get me out of here",
    // without touching the dialog's own close affordance.
    lastIntent = {type: "outside_click", at: Date.now()};
};

/**
 * Installs the capture-phase listeners. Capture phase so the intent is recorded
 * even when the dialog stops propagation on its way to closing itself.
 *
 * Returns an uninstall function; call it from the mounting effect's cleanup.
 * Client-only — the caller must not run this during SSR.
 */
export const installCloseIntentTracker = (): (() => void) => {
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);

    return () => {
        document.removeEventListener("pointerdown", onPointerDown, true);
        document.removeEventListener("keydown", onKeyDown, true);
        lastIntent = null;
    };
};

/**
 * Maps an orchestrator close result onto the spec's four `close_type` values.
 *
 * `success` and every non-user reason (`replaced`, `programmatic`, `cancelled`,
 * `dependency-failed`) are decided by the engine and never consult the heuristic.
 */
export const resolveCloseType = (result: DialogResult | undefined): CloseType => {
    if (result?.success) return "success";
    if (result?.reason !== "user") return "system";

    const intent = lastIntent;
    if (!intent || Date.now() - intent.at > INTENT_TTL_MS) return "system";
    return intent.type;
};
